import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { MerchReportService } from '../../services/merchReport.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import {
    IOrderBook, IOrderDashboardRow, ISampleTracker,
} from '../../interfaces/garments/merchReport.interface';
import { TNA_MILESTONE_TYPE_LABELS } from '../../interfaces/garments/tnaMilestone.interface';
import { enumLabel } from '../../utils/garmentsLabels';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import StatTile from '../dashboard/charts/StatTile';
import HBars from '../dashboard/charts/HBars';
import Columns from '../dashboard/charts/Columns';
import HStack100 from '../dashboard/charts/HStack100';
import { compactMoney } from '../dashboard/charts/axisPadding';

const OPEN_STATUSES = ['DRAFT', 'CONFIRMED', 'IN_PRODUCTION'];
const SHIP_WINDOW_DAYS = 30;
const MAX_MONTHS = 12;

const fmtInt = (n: number) => n.toLocaleString('en-US');

function addDays(iso: string, days: number): string {
    const [y, m, d] = iso.split('-').map(Number);
    return toLocalIsoDate(new Date(y, m - 1, d + days));
}

/** Contiguous YYYY-MM keys from `from` to `to` inclusive (capped). */
function monthRange(from: string, to: string): string[] {
    const out: string[] = [];
    let [y, m] = from.split('-').map(Number);
    const [ty, tm] = to.split('-').map(Number);
    while ((y < ty || (y === ty && m <= tm)) && out.length < MAX_MONTHS) {
        out.push(`${y}-${String(m).padStart(2, '0')}`);
        m += 1;
        if (m > 12) { m = 1; y += 1; }
    }
    return out;
}

function Section({ title, subtitle, children, ...rest }: { title: string; subtitle?: string; children: React.ReactNode; 'data-help-section'?: string }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }} {...rest}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{subtitle}</Typography>}
            <Box sx={{ mt: subtitle ? 0 : 1 }}>{children}</Box>
        </Paper>
    );
}

function Empty({ text }: { text: string }) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{text}</Typography>;
}

function OrderLink({ row }: { row: IOrderDashboardRow }) {
    return (
        <Link component={RouterLink} to={`/merchandising/orders/${row.orderId}`} underline="hover" sx={{ fontWeight: 600 }}>
            {row.orderNumber}
        </Link>
    );
}

/**
 * Merchandising dashboard — KPI tiles, charts and action lists computed client-side from the
 * existing merchandising report endpoints (order dashboard, order book, sample tracker). Each
 * endpoint is fetched independently so one failure only blanks the widgets that depend on it.
 */
export function MerchandisingDashboard() {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState<IOrderDashboardRow[] | null>(null);
    const [truncated, setTruncated] = useState(false);
    const [book, setBook] = useState<IOrderBook | null>(null);
    const [samples, setSamples] = useState<ISampleTracker | null>(null);
    const [draftBook, setDraftBook] = useState<IOrderBook | null>(null);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setLoading(false); return; }
        setLoading(true);
        const [o, b, s, d] = await Promise.allSettled([
            MerchReportService.getOrderDashboard(institutionId, { includeAll: true }),
            MerchReportService.getOrderBook(institutionId),
            MerchReportService.getSampleTracker(institutionId),
            // The order book leaves DRAFT orders out by default; ask for them explicitly.
            MerchReportService.getOrderBook(institutionId, { status: 'DRAFT' }),
        ]);
        if (o.status === 'fulfilled') { setOrders(o.value.rows); setTruncated(o.value.truncated); }
        else toast.showApiError(o.reason, 'Failed to load orders');
        if (b.status === 'fulfilled') setBook(b.value);
        else toast.showApiError(b.reason, 'Failed to load order value');
        if (s.status === 'fulfilled') setSamples(s.value);
        else toast.showApiError(s.reason, 'Failed to load samples');
        if (d.status === 'fulfilled') setDraftBook(d.value);
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const today = toLocalIsoDate();
    const horizon = addDays(today, SHIP_WINDOW_DAYS);

    const stats = useMemo(() => {
        if (!orders) return null;
        const open = orders.filter(r => OPEN_STATUSES.includes(r.status));
        const delayed = open.filter(r => r.delayed)
            .sort((a, b) => (a.nextMilestoneTargetDate ?? '9999').localeCompare(b.nextMilestoneTargetDate ?? '9999'));
        const shippingSoon = open.filter(r => r.shipDate >= today && r.shipDate <= horizon)
            .sort((a, b) => a.shipDate.localeCompare(b.shipDate));
        const pastShip = open.filter(r => r.shipDate < today).length;
        const quantity = open.reduce((sum, r) => sum + (r.totalQuantity ?? 0), 0);

        const statusCounts = ['DRAFT', 'CONFIRMED', 'IN_PRODUCTION', 'SHIPPED', 'CLOSED', 'CANCELLED']
            .map(s => ({ label: enumLabel(s), value: orders.filter(r => r.status === s).length }))
            .filter(d => d.value > 0);

        const byBuyer = new Map<string, { qty: number; count: number }>();
        open.forEach(r => {
            const cur = byBuyer.get(r.buyerName) ?? { qty: 0, count: 0 };
            byBuyer.set(r.buyerName, { qty: cur.qty + (r.totalQuantity ?? 0), count: cur.count + 1 });
        });
        const buyers = Array.from(byBuyer.entries())
            .map(([label, v]) => ({
                label, value: v.qty,
                tooltip: [{ label: 'Pieces', value: fmtInt(v.qty) }, { label: 'Orders', value: String(v.count) }],
            }))
            .sort((a, b) => b.value - a.value).slice(0, 8);

        let months: { label: string; value: number }[] = [];
        if (open.length > 0) {
            const keys = open.map(r => r.shipDate.slice(0, 7)).sort();
            const currentKey = today.slice(0, 7);
            const range = monthRange(keys[0] < currentKey ? keys[0] : currentKey, keys[keys.length - 1]);
            months = range.map(k => ({ label: k, value: open.filter(r => r.shipDate.startsWith(k)).length }));
        }

        const health = OPEN_STATUSES
            .map(s => {
                const inStatus = open.filter(r => r.status === s);
                const late = inStatus.filter(r => r.delayed).length;
                return { label: enumLabel(s), segments: [inStatus.length - late, late], total: inStatus.length };
            })
            .filter(r => r.total > 0);

        const byMilestone = new Map<string, number>();
        delayed.forEach(r => {
            const key = r.nextMilestoneType ? (TNA_MILESTONE_TYPE_LABELS[r.nextMilestoneType] ?? r.nextMilestoneType) : 'No milestone';
            byMilestone.set(key, (byMilestone.get(key) ?? 0) + 1);
        });
        const delayedByMilestone = Array.from(byMilestone.entries())
            .map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);

        return {
            open, delayed, shippingSoon, pastShip, quantity, statusCounts, buyers, months, health, delayedByMilestone,
            inProduction: open.filter(r => r.status === 'IN_PRODUCTION').length,
        };
    }, [orders, today, horizon]);

    // Order value per currency (never summed across currencies): the largest currency is the
    // headline, the others are listed underneath.
    const value = useMemo(() => {
        if (!book || book.rows.length === 0) return null;
        const byCurrency = new Map<string, number>();
        book.rows.forEach(r => {
            if (r.totalValue == null) return;
            const cur = r.currencyCode || '';
            byCurrency.set(cur, (byCurrency.get(cur) ?? 0) + r.totalValue);
        });
        const list = Array.from(byCurrency.entries()).sort((a, b) => b[1] - a[1]);
        return list.length ? list : null;
    }, [book]);

    // Draft orders: count from the order dashboard, value per currency from the DRAFT-only order
    // book (never summed across currencies; largest currency is the headline).
    const draft = useMemo(() => {
        if (!orders) return null;
        const count = orders.filter(r => r.status === 'DRAFT').length;
        const byCurrency = new Map<string, number>();
        (draftBook?.rows ?? []).forEach(r => {
            if (r.status !== 'DRAFT' || r.totalValue == null) return;
            const cur = r.currencyCode || '';
            byCurrency.set(cur, (byCurrency.get(cur) ?? 0) + r.totalValue);
        });
        return { count, values: Array.from(byCurrency.entries()).sort((a, b) => b[1] - a[1]) };
    }, [orders, draftBook]);

    const sampleStats = useMemo(() => {
        if (!samples) return null;
        const count = (...st: string[]) => samples.rows.filter(r => st.includes(r.status)).length;
        return {
            awaiting: count('SENT', 'RESUBMITTED'),
            overdue: samples.rows.filter(r => r.overdue).length,
            statuses: samples.statusTotals.filter(t => t.count > 0)
                .map(t => ({ label: enumLabel(t.status), value: t.count })),
        };
    }, [samples]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
    }

    const noOrders = !stats || stats.open.length === 0 && orders?.length === 0;

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} data-help-page="merchandising.dashboard.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h5">Merchandising Dashboard</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button data-help="merchandising.dashboard.orderTable" component={RouterLink} to="/merchandising/reports/order-dashboard" size="small" variant="contained">View order table</Button>
                    <Button data-help="merchandising.dashboard.orderBook" component={RouterLink} to="/merchandising/reports/order-book" size="small" variant="outlined">Order book</Button>
                    <Button data-help="merchandising.dashboard.sampleTracker" component={RouterLink} to="/merchandising/reports/sample-tracker" size="small" variant="outlined">Sample tracker</Button>
                    <Button data-help="merchandising.dashboard.sampleWorkload" component={RouterLink} to="/merchandising/reports/sample-workload" size="small" variant="outlined">Sample workload</Button>
                </Stack>
            </Stack>

            {truncated && (
                <Alert severity="warning" sx={{ mb: 2 }} data-help-section="merchandising.dashboard.notice">Order data was capped by the server — figures may understate the total.</Alert>
            )}

            {noOrders && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 2 }} data-testid="merch-dashboard-empty" data-help-section="merchandising.dashboard.empty">
                    <Typography variant="h6" gutterBottom>No orders yet</Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        Create a buyer order to see order status, shipments and T&A health here.
                    </Typography>
                    <Button data-help="merchandising.dashboard.newOrder" component={RouterLink} to="/merchandising/orders/new" variant="contained">New order</Button>
                </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 1, mb: 2 }} data-help-section="merchandising.dashboard.tiles">
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {stats && (
                        <>
                            <StatTile data-help="merchandising.dashboard.tile.openOrders" label="Open orders" value={stats.open.length} sub="Draft, confirmed, in production" />
                            <StatTile data-help="merchandising.dashboard.tile.pieces" label="Pieces on order" value={fmtInt(stats.quantity)} />
                            {value && (
                                <StatTile data-help="merchandising.dashboard.tile.orderValue"
                                    label="Order value" unit={value[0][0] || undefined} value={formatMoney(value[0][1])}
                                    sub={value.length > 1 ? value.slice(1).map(([cur, v]) => `+ ${cur} ${compactMoney(v)}`).join('  ') : undefined}
                                />
                            )}
                            <StatTile data-help="merchandising.dashboard.tile.inProduction" label="In production" value={stats.inProduction} />
                            <StatTile data-help="merchandising.dashboard.tile.delayed" label="Delayed orders" value={stats.delayed.length} tone={stats.delayed.length > 0 ? 'warning' : 'good'} sub="Behind on T&A" />
                            <StatTile data-help="merchandising.dashboard.tile.shipping30" label="Shipping in 30 days" value={stats.shippingSoon.length} sub={stats.pastShip > 0 ? `${stats.pastShip} past ship date` : undefined} tone={stats.pastShip > 0 ? 'warning' : 'default'} />
                        </>
                    )}
                    {draft && (
                        <StatTile data-help="merchandising.dashboard.tile.draftOrders"
                            label="Draft orders" value={draft.count}
                            sub={draft.values.length > 0
                                ? draft.values.map(([cur, v]) => `${cur ? cur + ' ' : ''}${formatMoney(v)}`).join('  ')
                                : 'Not yet confirmed'}
                        />
                    )}
                    {sampleStats && (
                        <StatTile data-help="merchandising.dashboard.tile.samplesAwaiting" label="Samples awaiting buyer" value={sampleStats.awaiting} sub={sampleStats.overdue > 0 ? `${sampleStats.overdue} overdue` : undefined} tone={sampleStats.overdue > 0 ? 'warning' : 'default'} />
                    )}
                    {!stats && !sampleStats && <Typography color="text.secondary" sx={{ p: 1.5 }}>Figures unavailable.</Typography>}
                </Stack>
            </Paper>

            {stats && !noOrders && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2, mb: 2 }}>
                    <Section data-help-section="merchandising.dashboard.byStatus" title="Orders by status" subtitle="All orders, including shipped, closed and cancelled">
                        <HBars data={stats.statusCounts} />
                    </Section>
                    <Section data-help-section="merchandising.dashboard.topBuyers" title="Top buyers by open pieces" subtitle="Open orders only">
                        {stats.buyers.length === 0 ? <Empty text="No open orders." /> : <HBars data={stats.buyers} vFmt={fmtInt} />}
                    </Section>
                    <Section data-help-section="merchandising.dashboard.shipMonths" title="Shipments by month" subtitle="Open orders by ship-date month">
                        {stats.months.length === 0 ? <Empty text="No open orders to ship." />
                            : <Columns data={stats.months} max={Math.ceil(Math.max(1, ...stats.months.map(m => m.value)) / 4) * 4} tickFmt={n => (Number.isInteger(n) ? String(n) : '')} />}
                    </Section>
                    <Section data-help-section="merchandising.dashboard.taHealth" title="T&A health" subtitle="Share of open orders on track (blue) vs delayed (amber), by status">
                        {stats.health.length === 0 ? <Empty text="No open orders." />
                            : <HStack100 rows={stats.health} seriesLabels={['On track', 'Delayed']} colorIndices={[0, 3]} />}
                    </Section>
                    <Section data-help-section="merchandising.dashboard.delayedMilestone" title="Delayed orders by next milestone" subtitle="Where the delays are stuck">
                        {stats.delayedByMilestone.length === 0 ? <Empty text="No delayed orders." /> : <HBars data={stats.delayedByMilestone} />}
                    </Section>
                    <Section data-help-section="merchandising.dashboard.sampleStatus" title="Samples by status" subtitle="All sample requests">
                        {!sampleStats ? <Empty text="Sample data unavailable." />
                            : sampleStats.statuses.length === 0 ? <Empty text="No sample requests yet." />
                                : <HBars data={sampleStats.statuses} />}
                    </Section>
                    <Section data-help-section="merchandising.dashboard.delayedList" title="Delayed orders — needs attention" subtitle="Earliest missed milestone first">
                        {stats.delayed.length === 0 ? <Empty text="Nothing delayed. Nice." /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                {stats.delayed.slice(0, 5).map(r => (
                                    <Box key={r.orderId} data-testid={`delayed-${r.orderId}`}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <OrderLink row={r} />
                                            <Typography variant="body2" color="text.secondary">{r.buyerName}</Typography>
                                            <Chip size="small" color="error" label={`${r.overdueCount} overdue`} />
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            {r.nextMilestoneType
                                                ? `${TNA_MILESTONE_TYPE_LABELS[r.nextMilestoneType] ?? r.nextMilestoneType}${r.nextMilestoneTargetDate ? ` · ${formatDate(r.nextMilestoneTargetDate)}` : ''}`
                                                : 'No upcoming milestone'} · ships {formatDate(r.shipDate)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Section>
                    <Section data-help-section="merchandising.dashboard.shippingSoon" title="Shipping soon" subtitle={`Next ${SHIP_WINDOW_DAYS} days`}>
                        {stats.shippingSoon.length === 0 ? <Empty text="No shipments due in the next 30 days." /> : (
                            <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                {stats.shippingSoon.slice(0, 5).map(r => (
                                    <Stack key={r.orderId} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`shipping-${r.orderId}`}>
                                        <OrderLink row={r} />
                                        <Typography variant="body2" color="text.secondary">{r.buyerName}</Typography>
                                        <Box sx={{ flex: 1 }} />
                                        <Typography variant="body2">{formatDate(r.shipDate)}</Typography>
                                        <Chip size="small" label={`${r.progressPct}% T&A`} color={r.delayed ? 'error' : 'default'} />
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Section>
                </Box>
            )}
        </Box>
    );
}

export default MerchandisingDashboard;
